import { CaretDown } from "@carbon/icons-react";
import cx from "classnames";
import Link from "next/link";
import React, { type HTMLAttributes, forwardRef, useCallback, useMemo, useState } from "react";

import type { PageMetadata } from "@moment/api-collab/api-types";
import { useTheme } from "@moment/design-system/ThemeProvider";

import { useDocumentActor } from "~/state";
import { DEFAULT_PAGE_TITLE } from "~/strings";
import { type UseNavigateToCanvasProps, canvasLinkHref } from "~/utils/helpers/url";

import { useEasel } from "../../easel";
import { useEaselCurrentPage } from "../../hooks/useEaselCurrentPage";
import { useResponsive } from "../../hooks/useResponsive";
import { EditInPlaceInput } from "../EditInPlaceInput";
import { useNavigationPane } from "../NavigationPaneContext";
import { PagePopOver } from "../PagePopOver";
import type { TreeItem as TreeItemType } from "../types";

export interface Props extends Omit<HTMLAttributes<HTMLLIElement>, "id"> {
	item: TreeItemType<PageMetadata>;
	canvasId: string;
	dragging?: boolean;
	childCount?: number;
	clone?: boolean;
	collapsed?: boolean;
	allowDelete: boolean;
	depth: number;
	ghost?: boolean;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	handleProps?: any;
	indentationWidth: number;
	onCollapse?(): void;
	wrapperRef?(node: HTMLLIElement): void;
}

const BASE_PADDING_PX = 36;

// eslint-disable-next-line react/display-name
export const TreeItem = forwardRef<HTMLDivElement, Props>(
	(
		{
			item,
			canvasId: documentId,
			dragging,
			childCount,
			clone,
			depth,
			ghost,
			handleProps,
			indentationWidth,
			collapsed,
			onCollapse,
			allowDelete,
			style,
			wrapperRef,
			...props
		},
		ref
	) => {
		const { resolvedTheme } = useTheme();

		const selectedColor =
			resolvedTheme === "light" ? "bg-brand-translucent-active" : "bg-primary-raised";

		const { data: page } = item;
		const { workspaceName } = useEasel();

		const documentActor = useDocumentActor(documentId);

		const renamePage = useCallback(
			async (newName: string) => {
				documentActor?.send({
					type: "page.nameChanged",
					payload: {
						pageId: page.id,
						newName,
					},
				});
			},
			[page.id, documentActor]
		);

		const {
			isReadOnly: _isReadOnly,
			renamingPageID,
			setRenamingPageID,
			unsetRenamingPageID,
		} = useNavigationPane();
		const { isTouchDevice } = useResponsive();
		const isReadOnly = useMemo(
			() => _isReadOnly || isTouchDevice,
			[_isReadOnly, isTouchDevice]
		);

		const currentPage = useEaselCurrentPage();

		const originalTitle = page.title;
		const isRenaming = !isReadOnly && page.id == renamingPageID;
		const [renamingTitle, setRenamingTitle] = useState(originalTitle);
		const title = isRenaming ? renamingTitle : originalTitle;

		const handleOnRenameInput = () => {
			if (isReadOnly) {
				return;
			}

			setRenamingTitle(originalTitle);
			setRenamingPageID(page.id);
		};

		const revertTitle = () => {
			setRenamingTitle(originalTitle);
			unsetRenamingPageID();
		};

		const saveTitle = async () => {
			if (isReadOnly) {
				return;
			}

			void renamePage(title);

			unsetRenamingPageID();
		};

		const handleOnKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
			switch (e.key) {
				case "Enter":
					await saveTitle();
					break;
				case "Esc":
				case "Escape":
					revertTitle();
					break;
			}
		};

		const handleOnBlur = async () => {
			await saveTitle();
		};

		const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
			e.currentTarget.select();
		};

		const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			setRenamingTitle(e.target.value);
		};

		const navProps: UseNavigateToCanvasProps = useMemo(
			() => ({
				workspaceName,
				pageSlug: page.slug,
				version: "",
				type: "CanvasById",
				id: documentId,
			}),
			[page.slug, workspaceName, documentId]
		);
		const pageLink = canvasLinkHref(navProps);

		const paddingPxBase = onCollapse ? 12 : BASE_PADDING_PX;
		const paddingPxForIndent = indentationWidth * depth;

		return (
			<li
				className={cx(
					"relative",
					"list-none",
					"my-px",
					"mx-2",
					clone && "select-none pointer-events-none"
				)}
				ref={wrapperRef}
				draggable={!isReadOnly}
				{...props}
			>
				<div
					className={cx(
						"relative",
						"group",
						"flex",
						"items-center",
						"text-sm",
						"h-8",
						"rounded",
						!dragging && currentPage?.id === item.data.id
							? selectedColor
							: "hover:bg-button-normal-secondary-hover"
					)}
					ref={ref}
					style={{
						...style,
						paddingLeft: `${paddingPxBase + paddingPxForIndent}px`,
					}}
				>
					{ghost && (
						<div
							className="absolute left-0 top-0 w-full"
							style={{ paddingLeft: `${BASE_PADDING_PX + paddingPxForIndent}px` }}
						>
							<div
								className="border-brand absolute rounded-full"
								style={{
									borderWidth: "4px",
									top: "-4px",
									marginLeft: "-6px",
									width: "8px",
									height: "8px",
								}}
							/>
							<div
								className="bg-brand absolute w-full rounded-r-full"
								style={{ height: "4px", top: "-2px", marginLeft: "-4px" }}
							/>
							{childCount && childCount > 1 ? (
								<div
									className={cx(
										"bg-brand",
										"text-inverse-primary",
										"absolute",
										"-top-3",
										"right-0",
										"flex",
										"h-6",
										"w-6",
										"items-center",
										"justify-center",
										"rounded-full",
										"text-xs",
										"font-bold"
									)}
								>
									{childCount}
								</div>
							) : null}
						</div>
					)}

					{onCollapse && (
						<button
							onClick={onCollapse}
							className={cx(
								"mr-2",
								"[&>svg]:fill-icon-default",
								"transition-all",
								"duration-300",
								"ease-in-out",
								{ "-rotate-90": collapsed }
							)}
						>
							<CaretDown />
						</button>
					)}

					<Link
						{...handleProps}
						href={pageLink}
						title={!isRenaming && originalTitle ? originalTitle : DEFAULT_PAGE_TITLE}
						data-cypress="draggable-handle"
						onDoubleClick={handleOnRenameInput}
						shallow
						onKeyDown={(e) => {
							if (renamingTitle) {
								return;
							}

							handleProps.onKeyDown(e);
						}}
						onPointerDown={(e) => {
							// No dnd if we're renaming the page.
							if (isRenaming) {
								return;
							}

							handleProps.onPointerDown?.(e);
						}}
						className={cx(
							"min-w-0 grow shrink h-full text-left flex items-center no-underline text-primary hover:text-primary focus:text-primary focus:outline-none active:ring-0 focus:ring-0 transition-none",
							!isReadOnly && "cursor-grab",
							!isRenaming && "hover:underline"
						)}
						tabIndex={0}
					>
						{isRenaming && (
							<EditInPlaceInput
								placeholder={DEFAULT_PAGE_TITLE}
								value={title}
								onClick={(e) => {
									// When renaming, DO NOT let clicks trigger the link. When a
									// user clicks a character while renaming they want to move the
									// cursor, not follow the underlying link.
									e.preventDefault();
									e.stopPropagation();
								}}
								onKeyDown={handleOnKeyDown}
								onChange={handleOnChange}
								onBlur={handleOnBlur}
								onFocus={handleOnFocus}
								// eslint-disable-next-line jsx-a11y/no-autofocus
								autoFocus
							/>
						)}

						{!isRenaming && originalTitle && (
							<div className="min-w-0 truncate">{originalTitle}</div>
						)}

						{!isRenaming && !originalTitle && (
							<span className="text-secondary truncate pr-1 italic">
								{DEFAULT_PAGE_TITLE}
							</span>
						)}
					</Link>

					{!isReadOnly && (
						<div className={cx("ml-1")}>
							<PagePopOver
								page={page}
								renamingPage={isRenaming}
								onRenamePage={handleOnRenameInput}
								allowDelete={allowDelete}
							/>
						</div>
					)}
				</div>
			</li>
		);
	}
);
